import { z } from 'zod';
import { compararDinheiro, somarDinheiro, subtrairDinheiro } from './aritmetica.ts';
import { dataSchema, dinheiroSchema } from './comum.ts';
import { janelaDeUltimosDias } from './painel-compras.ts';

/**
 * O que os painéis de título compartilham — hoje são **quatro**.
 *
 * Está para eles como `titulos.ts` está para os dois módulos de título, e nasceu
 * pelo mesmo motivo: as faixas de vencimento, a janela das baixas, o corte do
 * topo dos gráficos e a fatia "Outros" não sabem de que lado do caixa estão. Uma
 * dívida que vence em nove dias e um crédito que vence em nove dias caem na
 * mesma faixa, e ela se calcula da mesma forma.
 *
 * O arquivo começou servindo a dois — o painel de contas a pagar e o de contas a
 * receber. O **confronto** (`confrontoSchema`, `confrontar`) morou em
 * `painel-financeiro.ts` enquanto só aquele painel cruzava os dois lados, e veio
 * para cá quando o painel de obras passou a cruzá-los também: cruzar "o que a
 * obra deve" com "o que a obra tem a receber" é a mesma operação que cruzar as
 * duas pontas do caixa da empresa, e a segunda cópia seria a que um dia
 * subtrairia na ordem inversa.
 *
 * O que **não** veio junto foi o confronto **por faixa** (`fatiaComparadaSchema`,
 * `serieComparada`). Ele continua em `painel-financeiro.ts` porque só aquele
 * painel o usa: o de obras confronta os totais de um período que o usuário
 * escolhe, e não a distância até hoje. Trazê-lo para cá por simetria criaria um
 * compartilhamento entre um usuário e nenhum.
 *
 * Duas cópias disso divergiriam na primeira correção que alcançasse só uma — e o
 * sintoma seria o mais difícil de investigar que existe neste projeto: o painel
 * de um lado somando 30 dias com hoje incluído e o do outro sem, com os dois
 * parecendo certos isoladamente.
 *
 * ## O que **não** mora aqui, e por quê
 *
 * - **O recorte da consulta.** São quatro linhas de Zod idênticas nos dois
 *   painéis, e ainda assim cada módulo escreve a sua. A rota é a fronteira
 *   pública de cada um, e o dia em que o receber ganhar um filtro de cliente —
 *   que o pagar não tem — a divisão já está feita.
 * - **O formato da resposta.** Ali os nomes dizem de que lado se fala: `pagas` e
 *   `pagasPorFornecedor` de um lado, `recebidas` e `recebidasPorCliente` do
 *   outro. Um `baixasPorContraparte` genérico serviria aos dois e obrigaria toda
 *   tela a traduzir de volta para a palavra que o usuário usa.
 * - **O que se soma.** Saldo devedor e saldo a receber são a mesma coluna com
 *   sentidos opostos, e quem escreve o `SELECT` é o repositório de cada módulo.
 */

/* -------------------------------------------------------------------------- */
/* As faixas de vencimento                                                    */
/* -------------------------------------------------------------------------- */

/**
 * As faixas, na ordem do tempo — e ela tem significado.
 *
 * O atraso vem primeiro porque é o que pede ação hoje, e as demais avançam para
 * o futuro. Ordenar por valor faria as barras trocarem de lugar a cada abertura
 * da tela, e quem confere perderia a comparação com a leitura de ontem.
 *
 * **As faixas são exclusivas**: o título vence em uma e só uma. "Próximos 30
 * dias" aqui **não** inclui a semana nem a quinzena — é o 16º ao 30º dia. Foi
 * pedido assim, e é o que torna a soma possível: com faixas cumulativas, somar
 * as barras contaria o mesmo título cinco vezes.
 */
export const FAIXAS_VENCIMENTO = [
  'ATRASADO',
  'SEMANA',
  'QUINZENA',
  'DIAS_30',
  'DIAS_60',
  'DIAS_90',
  'ALEM_90',
] as const;

export const faixaDeVencimentoSchema = z.enum(FAIXAS_VENCIMENTO);
export type FaixaDeVencimento = z.infer<typeof faixaDeVencimentoSchema>;

/**
 * O último dia de cada faixa **fechada**, contado a partir de hoje.
 *
 * `ATRASADO` e `ALEM_90` não estão aqui porque são abertas nas pontas — uma não
 * tem começo, a outra não tem fim —, e o `Exclude` é o que garante isso: quem
 * acrescentar uma faixa nova ao enum não compila enquanto não decidir se ela é
 * fechada e quantos dias tem.
 *
 * A semana são **7 dias corridos a partir de hoje**, e não "até domingo". A
 * segunda leitura casa melhor com quem fecha pagamento na sexta, mas faria a
 * primeira faixa mudar de tamanho todo dia — no sábado ela teria dois dias, e o
 * número da semana despencaria sem que nada tivesse sido pago.
 */
export const DIAS_DA_FAIXA: Readonly<
  Record<Exclude<FaixaDeVencimento, 'ATRASADO' | 'ALEM_90'>, number>
> = {
  SEMANA: 7,
  QUINZENA: 15,
  DIAS_30: 30,
  DIAS_60: 60,
  DIAS_90: 90,
};

/**
 * Os rótulos, e o cuidado que eles exigem.
 *
 * São **neutros de propósito**: "Em atraso" e "Nesta semana" valem para a dívida
 * e para o crédito. Um rótulo que dissesse "A pagar nesta semana" obrigaria este
 * arquivo a ter dois mapas, e a segunda cópia é onde "Na próxima quinzena"
 * viraria "Próxima quinzena" em uma das telas.
 */
export const ROTULO_FAIXA_VENCIMENTO: Readonly<Record<FaixaDeVencimento, string>> = {
  ATRASADO: 'Em atraso',
  SEMANA: 'Nesta semana',
  QUINZENA: 'Na próxima quinzena',
  DIAS_30: 'De 16 a 30 dias',
  DIAS_60: 'De 31 a 60 dias',
  DIAS_90: 'De 61 a 90 dias',
  ALEM_90: 'Depois de 90 dias',
};

/**
 * A dica de cada faixa, exibida sob o número.
 *
 * Elas dizem o **recorte**, não o valor: "sem os da semana" é a informação que
 * impede alguém de somar quinzena com semana e chegar ao dobro. Neutras como os
 * rótulos — o que vence é o título, seja ele dívida ou crédito.
 */
export const DICA_FAIXA_VENCIMENTO: Readonly<Record<FaixaDeVencimento, string>> = {
  ATRASADO: 'Venceu e ainda tem saldo. É o único número desta tela que olha para trás.',
  SEMANA: 'Vence de hoje ao sétimo dia, hoje incluído.',
  QUINZENA: 'Do oitavo ao décimo quinto dia — sem os desta semana.',
  DIAS_30: 'Do décimo sexto ao trigésimo dia, sem as faixas anteriores.',
  DIAS_60: 'Do trigésimo primeiro ao sexagésimo dia, sem as faixas anteriores.',
  DIAS_90: 'Do sexagésimo primeiro ao nonagésimo dia, sem as faixas anteriores.',
  ALEM_90: 'Vence depois do nonagésimo dia. Existe para as faixas fecharem com o total.',
};

/* -------------------------------------------------------------------------- */
/* A janela                                                                   */
/* -------------------------------------------------------------------------- */

/** O período de uma faixa. `null` na ponta aberta — ver `FAIXAS_VENCIMENTO`. */
export interface PeriodoDaFaixa {
  faixa: FaixaDeVencimento;
  /** Primeiro dia contado. `null` em `ATRASADO`: não há começo. */
  de: string | null;
  /** Último dia contado. `null` em `ALEM_90`: não há fim. */
  ate: string | null;
}

export interface JanelaDasFaixas {
  /** Hoje, pelo relógio do servidor. É o corte entre o atraso e o resto. */
  hoje: string;
  /** As sete faixas, na ordem do enum, com o período de cada uma resolvido. */
  faixas: readonly PeriodoDaFaixa[];
}

/** `AAAA-MM-DD` a partir dos componentes **locais**. Mesma regra do painel de compras. */
function iso(data: Date): string {
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');

  return `${data.getFullYear()}-${mes}-${dia}`;
}

/** O dia `dias` à frente da meia-noite local de `base`, como `AAAA-MM-DD`. */
function diaRelativo(base: Date, dias: number): string {
  const data = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  data.setDate(data.getDate() + dias);

  return iso(data);
}

/**
 * As sete faixas resolvidas a partir do instante em que se pergunta.
 *
 * **Recebe o instante em vez de chamar `new Date()` por dentro**, como
 * `janelaDoPainelDeCompras`: é o que a torna testável sem congelar o relógio, e
 * o que permite ao teste conferir a virada de mês e a de ano.
 *
 * Os componentes são os **locais** (`getDate`), nunca `toISOString()`: as datas
 * do sistema são de negócio — o título vence no dia 10, não às 03h do dia 10 em
 * UTC —, e a janela aberta às 22h em São Paulo passaria a tratar como atrasado
 * o título que vence amanhã.
 *
 * A semana **inclui hoje**, e por isso vai de `hoje` a `hoje + 7`: o título que
 * vence hoje de tarde é o mais urgente que existe, e deixá-lo fora da primeira
 * faixa o mandaria para o atraso só na virada da meia-noite.
 */
export function janelaDasFaixas(agora: Date): JanelaDasFaixas {
  const hoje = diaRelativo(agora, 0);

  const fim = {
    SEMANA: diaRelativo(agora, DIAS_DA_FAIXA.SEMANA),
    QUINZENA: diaRelativo(agora, DIAS_DA_FAIXA.QUINZENA),
    DIAS_30: diaRelativo(agora, DIAS_DA_FAIXA.DIAS_30),
    DIAS_60: diaRelativo(agora, DIAS_DA_FAIXA.DIAS_60),
    DIAS_90: diaRelativo(agora, DIAS_DA_FAIXA.DIAS_90),
  } as const;

  return {
    hoje,
    faixas: [
      { faixa: 'ATRASADO', de: null, ate: diaRelativo(agora, -1) },
      { faixa: 'SEMANA', de: hoje, ate: fim.SEMANA },
      { faixa: 'QUINZENA', de: diaRelativo(agora, DIAS_DA_FAIXA.SEMANA + 1), ate: fim.QUINZENA },
      { faixa: 'DIAS_30', de: diaRelativo(agora, DIAS_DA_FAIXA.QUINZENA + 1), ate: fim.DIAS_30 },
      { faixa: 'DIAS_60', de: diaRelativo(agora, DIAS_DA_FAIXA.DIAS_30 + 1), ate: fim.DIAS_60 },
      { faixa: 'DIAS_90', de: diaRelativo(agora, DIAS_DA_FAIXA.DIAS_60 + 1), ate: fim.DIAS_90 },
      { faixa: 'ALEM_90', de: diaRelativo(agora, DIAS_DA_FAIXA.DIAS_90 + 1), ate: null },
    ],
  };
}

/**
 * O tamanho da janela dos gráficos de **baixas** — o que se pagou, o que se
 * recebeu —, em dias, hoje incluído.
 *
 * Trinta, como o painel de compras, e pelo mesmo motivo: é o mês corrido de
 * trabalho, curto o bastante para o gráfico falar do agora. Constante, e não
 * parâmetro — um seletor de período aqui transformaria o painel em relatório, e
 * relatório com filtro de data já existe: é a listagem do título, que tem `de` e
 * `ate`.
 */
export const JANELA_BAIXAS_DIAS = 30;

/**
 * A janela das baixas.
 *
 * Delega a `janelaDeUltimosDias`, do painel de compras, em vez de repetir a
 * aritmética: a regra do dia local e o "volta 29 para contar 30" já estão
 * testados lá, e uma segunda cópia erraria o dia na primeira correção que
 * alcançasse só uma delas.
 */
export function janelaDasBaixas(agora: Date): { de: string; ate: string } {
  return janelaDeUltimosDias(agora, JANELA_BAIXAS_DIAS);
}

/* -------------------------------------------------------------------------- */
/* Os totais e as faixas                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Quantos títulos e quanto dinheiro — o par que todo indicador destas telas tem.
 *
 * Os dois juntos, e não só o valor, porque respondem a perguntas diferentes:
 * R$ 80.000,00 em três títulos é uma negociação, e em quatrocentos é uma rotina
 * de cobrança. Quem abre o painel decide o que fazer com base nos dois.
 */
export const totalDeTitulosSchema = z.object({
  quantidade: z.number().int().nonnegative(),
  valor: dinheiroSchema,
});

export type TotalDeTitulos = z.infer<typeof totalDeTitulosSchema>;

export const fatiaDaFaixaSchema = z.object({
  faixa: faixaDeVencimentoSchema,
  quantidade: z.number().int().nonnegative(),
  /** Soma do **saldo**, não do valor original. Ver o painel de cada módulo. */
  valor: dinheiroSchema,
  de: dataSchema.nullable(),
  ate: dataSchema.nullable(),
});

export type FatiaDaFaixa = z.infer<typeof fatiaDaFaixaSchema>;

const ZERO = '0.00';

/**
 * A série completa das faixas: **todas as sete**, inclusive as vazias.
 *
 * Sem isto o `GROUP BY` devolveria só as faixas com título, e o indicador
 * ausente se lê como "não consegui contar" em vez de "nada vence aí" — que é
 * justamente a informação boa quando o que falta é o atraso.
 *
 * Vive no contrato, e não no service, porque é o formato que a tela lê: se a API
 * um dia deixasse de completar a série, a tela mudaria de forma sem que nada
 * falhasse.
 */
export function serieDeFaixas(
  janela: JanelaDasFaixas,
  contagens: Iterable<readonly [string, { quantidade: number; valor: string }]>,
): FatiaDaFaixa[] {
  const mapa = new Map(contagens);

  return janela.faixas.map((periodo) => {
    const contagem = mapa.get(periodo.faixa);

    return {
      faixa: periodo.faixa,
      quantidade: contagem?.quantidade ?? 0,
      valor: contagem?.valor ?? ZERO,
      de: periodo.de,
      ate: periodo.ate,
    };
  });
}

/** O total de uma série de faixas — o "em aberto" reconstruído a partir dela. */
export function totalDasFaixas(fatias: readonly FatiaDaFaixa[]): TotalDeTitulos {
  return {
    quantidade: fatias.reduce((soma, fatia) => soma + fatia.quantidade, 0),
    valor: somarDinheiro(fatias.map((fatia) => fatia.valor)),
  };
}

/* -------------------------------------------------------------------------- */
/* As séries dos gráficos                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Quantas barras cabem num gráfico de agrupamento livre.
 *
 * Oito, e o resto vira uma barra só. O plano de contas tem dezenas de folhas e
 * um grupo tem centenas de fornecedores e de clientes: sem corte, o gráfico rola
 * sem fim e as barras de baixo ficam rentes ao zero. Com corte **e sem** a barra
 * do resto, o gráfico esconderia valor sem dizer — e é justamente o que faz
 * alguém somar as barras, não bater com o indicador do topo e abrir chamado.
 */
export const TOPO_DA_SERIE = 8;

/** O que se escreve quando o vínculo não resolveu nome nenhum. */
export const ROTULO_SEM_NOME = 'Não identificado';

export const fatiaDaSerieSchema = z.object({
  /** Id do agrupamento — conta contábil, fornecedor, cliente. Chave da barra. */
  chave: z.string(),
  rotulo: z.string(),
  valor: dinheiroSchema,
});

export type FatiaDaSerie = z.infer<typeof fatiaDaSerieSchema>;

export const serieDeValoresSchema = z.object({
  /** As maiores, em ordem decrescente de valor. No máximo `TOPO_DA_SERIE`. */
  fatias: z.array(fatiaDaSerieSchema),
  /**
   * O que ficou fora do topo, somado numa fatia só. Nulo quando nada sobrou.
   *
   * `grupos` é **quantos** agrupamentos ela resume, e não decoração: "Outros"
   * sozinho não diz se são duas contas ou duzentas, e a diferença muda o que se
   * faz com o gráfico.
   */
  outros: z.object({ valor: dinheiroSchema, grupos: z.number().int().positive() }).nullable(),
  /** O total do recorte inteiro — topo mais outros. É o que a legenda escreve. */
  total: dinheiroSchema,
});

export type SerieDeValores = z.infer<typeof serieDeValoresSchema>;

/**
 * Monta a série a partir do topo já ordenado pelo banco, do total e de quantos
 * agrupamentos existem.
 *
 * **"Outros" é subtração, não segunda consulta**: o total vem da mesma
 * agregação, por função de janela (`SUM(...) OVER ()`), que o Postgres calcula
 * antes do `LIMIT`. Somar o resto numa consulta à parte custaria uma segunda
 * varredura e abriria a chance de as duas discordarem por milissegundos de
 * diferença entre elas.
 *
 * **O resultado negativo é aparado em zero.** Ele não deveria acontecer — o
 * total é do mesmo `SELECT` que as fatias —, mas no contas a pagar a soma
 * ponderada por índice arredonda a centavo, e uma diferença de um centavo para
 * baixo desenharia uma barra negativa. Zero é a leitura certa: não sobrou nada.
 */
export function montarSerieDeValores(
  linhas: readonly { chave: string; rotulo: string | null; valor: string }[],
  total: string,
  grupos: number,
): SerieDeValores {
  const fatias = linhas.map((linha) => ({
    chave: linha.chave,
    rotulo: linha.rotulo ?? ROTULO_SEM_NOME,
    valor: linha.valor,
  }));

  const restantes = grupos - fatias.length;
  if (restantes <= 0) return { fatias, outros: null, total };

  const sobra = subtrairDinheiro(total, somarDinheiro(fatias.map((fatia) => fatia.valor)));

  return {
    fatias,
    outros: {
      valor: compararDinheiro(sobra, ZERO) < 0 ? ZERO : sobra,
      grupos: restantes,
    },
    total,
  };
}

/* -------------------------------------------------------------------------- */
/* O confronto dos dois lados                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Os dois lados de um período, e o que sobra.
 *
 * Serve ao **em aberto** — o que ainda vai acontecer — e ao **realizado** — o
 * que aconteceu nos últimos 30 dias. São perguntas opostas no tempo e a forma
 * da resposta é a mesma: quanto de um lado, quanto do outro, quanto sobra. Dois
 * schemas iguais com nomes diferentes divergiriam na primeira correção.
 *
 * Serve também aos **dois painéis que confrontam**: o financeiro, que cruza as
 * duas pontas do caixa da empresa, e o de obras, que cruza o custo com a receita
 * de um canteiro. O que muda entre eles é o que entra em cada lado — títulos
 * inteiros lá, a fatia rateada da obra aqui — e o período que os recorta, e as
 * duas coisas são decididas pelo `SELECT` de cada repositório, não por este
 * schema. É por ser tão pouco que ele serve aos dois.
 */
export const confrontoSchema = z.object({
  aPagar: totalDeTitulosSchema,
  aReceber: totalDeTitulosSchema,
  /** `aReceber − aPagar`. Negativo quando o lado que sai é maior. */
  liquido: dinheiroSchema,
});

export type Confronto = z.infer<typeof confrontoSchema>;

/** Monta o confronto a partir dos dois lados. A subtração num lugar só. */
export function confrontar(aPagar: TotalDeTitulos, aReceber: TotalDeTitulos): Confronto {
  return { aPagar, aReceber, liquido: subtrairDinheiro(aReceber.valor, aPagar.valor) };
}

/*
  O confronto **por faixa** — `fatiaComparadaSchema` e `serieComparada` — não
  mora aqui, e a assimetria é deliberada: ele tem um usuário só, o painel
  financeiro, e vive lá. O painel de obras usa o `Confronto` acima, mas não a
  série: ele compara os totais de um período que o usuário escolhe, e a faixa é a
  distância até hoje.

  Trazê-lo para cá "porque é da mesma família" criaria compartilhamento entre um
  usuário e nenhum — e o custo não é o arquivo, é a próxima pessoa que lê este e
  conclui que a faixa comparada é algo que todos os painéis fazem.
*/
